import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import { apiFetch } from '../api/api';
import { extractServiceCities, extractServiceList, getCityName } from '../utils/deliveryZones';
import { localizeField } from '../utils/mapOrder';
import { formatPrice, parseProductPrice } from '../utils/price';

function formatAmount(value) {
  return formatPrice(parseProductPrice(value));
}

function formatDiscountPercent(value) {
  const num = parseProductPrice(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Number.isInteger(num) ? String(num) : String(num);
}

function SectionTitle({ children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: 'secondary.main' }} />
      <Typography variant="subtitle1" fontWeight={800} color="text.secondary" letterSpacing={0.5}>
        {children}
      </Typography>
    </Stack>
  );
}

function OfferBadge({ isFree }) {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      icon={isFree ? <LocalShippingOutlinedIcon /> : <StarBorderOutlinedIcon />}
      label={isFree ? t('delivery_offer_type_free') : t('delivery_offer_type_discount')}
      sx={{
        fontWeight: 800,
        bgcolor: isFree ? 'success.light' : 'warning.light',
        color: isFree ? 'success.dark' : 'warning.dark',
        '& .MuiChip-icon': { color: 'inherit' },
      }}
    />
  );
}

function CityRow({ city, isLast }) {
  const { t, i18n } = useTranslation();
  const price = formatAmount(city.delivery_price);
  const name = getCityName(city, i18n.language);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 2,
        px: 2.5,
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <LocalShippingOutlinedIcon sx={{ color: 'primary.main' }} />
      </Box>
      <Typography variant="body1" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
        {name}
      </Typography>
      <Chip
        label={`${price} ${t('orders_currency')}`}
        sx={{
          fontWeight: 800,
          bgcolor: 'primary.light',
          color: 'primary.dark',
        }}
      />
    </Box>
  );
}

function OfferCard({ offer }) {
  const { t, i18n } = useTranslation();
  const minAmount = formatAmount(offer.min_amount);
  const discount = formatDiscountPercent(offer.discount_percent);
  const isFree = offer.type === 'free_delivery' || !discount;
  const title = localizeField(offer.title, i18n.language);

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: 2, borderColor: 'primary.light' },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: title ? 1.5 : 0 }}>
        <OfferBadge isFree={isFree} />
        <Chip
          label={`${minAmount} ${t('orders_currency')}`}
          variant="outlined"
          sx={{ fontWeight: 800, color: 'primary.main', borderColor: 'primary.light' }}
        />
      </Stack>
      {title ? (
        <Typography variant="body2" fontWeight={600} lineHeight={1.7} color="text.secondary">
          {title}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function DeliveryOffers() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const loadData = useCallback(async ({ soft = false } = {}) => {
    if (soft) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const [citiesRes, offersRes] = await Promise.all([
        apiFetch('/users/cities'),
        apiFetch('/users/offers'),
      ]);

      if (!citiesRes.ok || !offersRes.ok) {
        throw new Error('fetch_failed');
      }

      const [citiesJson, offersJson] = await Promise.all([citiesRes.json(), offersRes.json()]);
      setCities(extractServiceCities(citiesJson));
      setOffers(extractServiceList(offersJson));
    } catch {
      setError(t('delivery_info_load_failed'));
      setCities([]);
      setOffers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData, i18n.language]);

  useEffect(() => {
    if (location.pathname === '/offers') {
      const timer = window.setTimeout(() => {
        document.getElementById('offers')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [location.pathname, loading]);

  return (
    <Box sx={{ bgcolor: 'background.default', pb: 6, minHeight: '60vh' }}>
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', py: 3, mb: 4 }}>
        <Container maxWidth="lg">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" underline="hover" color="inherit">
              {t('app_name')}
            </Link>
            <Typography color="text.primary" fontWeight={600}>
              {t('delivery_info_title')}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #D4A574, #B8894A, #8F6A35)',
                  color: '#fff',
                  boxShadow: '0 4px 14px rgba(184,137,74,0.35)',
                  flexShrink: 0,
                }}
              >
                <LocalShippingOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" fontWeight={800}>
                  {t('delivery_info_title')}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('delivery_info_intro')}
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={() => loadData({ soft: true })}
              disabled={loading || refreshing}
              aria-label={t('orders_retry')}
              sx={{ flexShrink: 0 }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            textAlign: 'center',
            color: '#fff',
            background: 'linear-gradient(135deg, #C9A05A 0%, #B8894A 55%, #8F6A35 100%)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -36,
              right: -24,
              width: 110,
              height: 110,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.12)',
            }}
          />
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.35)',
            }}
          >
            <InfoOutlinedIcon />
          </Box>
          <Typography variant="h6" fontWeight={800} lineHeight={1.6}>
            {t('delivery_info_intro')}
          </Typography>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        ) : null}

        <Box id="delivery" sx={{ mb: 5 }}>
          <SectionTitle>{t('delivery_info_cities_section')}</SectionTitle>

          {loading ? (
            <Stack spacing={1}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : (
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              {cities.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 5, px: 3 }}>
                  {t('delivery_info_empty')}
                </Typography>
              ) : (
                cities.map((city, index) => (
                  <CityRow
                    key={city.id ?? `city-${index}`}
                    city={city}
                    isLast={index === cities.length - 1}
                  />
                ))
              )}
            </Paper>
          )}
        </Box>

        <Box id="offers" sx={{ mb: 4 }}>
          <SectionTitle>{t('delivery_info_offers_section')}</SectionTitle>

          {loading ? (
            <Stack spacing={2}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 3 }} />
              ))}
            </Stack>
          ) : offers.length === 0 ? (
            <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, p: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('delivery_info_empty')}</Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {offers.map((offer, index) => (
                <OfferCard key={offer.id ?? `offer-${index}`} offer={offer} />
              ))}
            </Stack>
          )}
        </Box>
      </Container>
    </Box>
  );
}
